// 安全整改(2026-08)：敏感参数(sssid/imei/sAccount 等)不再走 URL 查询串或请求体，
// 统一移至请求头。request.js 的 service、api2/restfulApi.ts、全局 axios 共用此逻辑。
const HEADER_MAP = {
  sssid: 'X-Sssid',
  sssids: 'X-Sssids',
  bindsssids: 'X-Sssids',
  imei: 'X-Imei',
  imeis: 'X-Imeis',
  deviceId: 'X-Device-Id',
  sFatherAccount: 'X-Father-Account',
  sChildAccount: 'X-Child-Account',
  targetAccount: 'X-Target-Account',
  userAccount: 'X-User-Account',
  sUserAccount: 'X-User-Account',
  ids: 'X-Ids',
}

// FormData 中的 imeis 等可能以 JSON 数组字符串提交，统一转成逗号分隔
function normalizeListValue(value) {
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      const arr = JSON.parse(value)
      if (Array.isArray(arr)) {
        return arr.join(',')
      }
    } catch (e) {
      // 忽略解析失败，按原值处理
    }
  }
  return value
}

// 后端尚未加固、各敏感参数仍从 URL 查询串读取的接口（反推自后端源码扫描，2026-08）。
// 命中白名单时：查询串参数保留，同时补请求头；其余接口一律移到请求头并从查询串删除。
// 采用失败安全方向：白名单漏加最多是查询串多带一个参数，不会破坏接口语义。
// 注：GPRSRecord GetList/Export 已随 568992f4 改走 X-Sssids 请求头，不在名单内。
const QUERY_PARAM_KEEP_URLS = {
  ids: [
    'AlarmEmailConfig/DeleteSettings',
    'ReminderEmailConfig/DeleteSettings',
    'User/DestroyUser',
  ],
  sssids: [
    'Command/GetCommands',
    'Movie/getUpdateInfo',
    'Template/RemoveItems',
    'Tracker/AddExistTrackers',
    'TrackerDefine/GetSensorStateNameList',
    'TrackerDefine/GetStateList',
    'TrackerDefine/ProcessRequest',
    'TrackerDefine/SearchItems',
    'Upload/GetActiveUploadsByDevices',
    'Vertex/BindVertex',
    'Vertex/GetBindList',
  ],
  imei: [
    'DrivingRatingRule/SetBindSettings',
    'Location/GetDetailPicAsync',
    'Location/GetThumbPicAsync',
    'Movie/recordUploadToFTP',
    'OilSet/SetBindSettings',
    'ReportEmailSet/SetBindSettings',
    'Tracker/AddExistTracker',
  ],
  imeis: [
    'Movie/getRecFileInfo',
    'Report/GetSupportedEvents',
    'Traffics/BatchTrafficReminderAction',
  ],
  sFatherAccount: [
    'Tracker/EditTracker',
    'Tracker/ExcelImport',
    'Tracker/ImportJudgment',
    'Tracker/NewTracker',
    'Tracker/RemoveTrackers',
    'User/AddExistUser',
    'User/EditUser',
    'User/ExcelImport',
    'User/NewUser',
  ],
  sChildAccount: [
    'Access/GetAllModel',
    'Access/GetModelV2',
    'Traffics/TrafficPkgAllocation',
    'User/Recover',
  ],
  userAccount: [
    'Login/GetCaptcha2Email',
    'Login/RecoverPassword',
    'Login/ResetPassword',
    'Login/SSOBindLogin',
    'Login/UserLogin',
    'Login/UserLoginBackend',
    'Login/UserLoginWithCaptcha',
    'Permissions/GetUserEffectivePermissions',
    'Permissions/GetUserTemplate',
    'Permissions/RemoveTemplateFromUser',
  ],
}

function shouldKeepParamInQuery(key, url) {
  const list = QUERY_PARAM_KEEP_URLS[key]
  if (!list) {
    return false
  }
  const target = url || ''
  return list.some((u) => {
    const i = target.indexOf(u)
    if (i === -1) {
      return false
    }
    const after = target[i + u.length]
    return !after || !/[A-Za-z0-9_]/.test(after)
  })
}

// 后端已加固、sAccount 改从 X-Account 请求头读取的接口（反推自后端源码扫描，2026-08）。
// 仅对这些接口从 URL/params 删除 sAccount；其余接口仍从查询串读取 sAccount，
// 删掉会导致目标账号语义丢失（如 Tracker/AddExistTrackers 授权给子账号、
// User/UpdateUserPassword 修改他人密码等）。User/UpdateUser 存在已加固/未加固两个重载，未列入（保留查询串两侧都能工作）。
const S_ACCOUNT_REMOVE_URLS = [
  'Access/GetEffectiveModelV2',
  'Access/GetModel',
  'Access/GetOneModelV2',
  'Alarm/GetModelByids',
  'AlarmEmailConfig/GetSetting',
  'AlarmEmailConfig/GetSettings',
  'AlarmEmailConfig/ToggleSetting',
  'Car/GetModel',
  'Certificate/GetDList',
  'Certificate/GetTList',
  'Common/UploadAvatarFile',
  'Common/UploadFile',
  'Driver/GetList',
  'Driver/GetModel',
  'DriverRFID/GetDriverByRFID',
  'DriverRFID/GetModel',
  'DriverRFID/GetRFIDsByDriver',
  'FOTA/ExportTaskExcel',
  'FOTA/GetCommandBufferList',
  'FOTA/GetFileList',
  'FOTA/GetFirmwareStatistics',
  'FOTA/GetSummary',
  'FOTA/GetTaskList',
  'FaceInfo/GetFaceDrivers',
  'FaceInfo/SetFaceDrivers',
  'FaceInfo/SyncFaceDrivers',
  'FtpSubAccount/GetModel',
  'IO/GetModel',
  'Maintenance/GetMaintenanceByIds',
  'Maintenance/GetMaintenanceCar',
  'Maintenance/GetMaintenanceContent',
  'Maintenance/GetMaintenanceNotice',
  'Maintenance/GetModel',
  'MaintenanceCost/GetMaintenanceCostByVehicle',
  'MaintenanceCost/GetModel',
  'OTACommand/GetList',
  'POI/GetModel',
  'PermissionTemplate/AppliedTemplates',
  'ReminderEmailConfig/GetSetting',
  'ReminderEmailConfig/GetSettings',
  'ReminderEmailConfig/ToggleSetting',
  'ReportCustom/GetModel',
  'ReportEmailSet/GetModel',
  'ReportEmailSet/GetReportEmailSetItems',
  'ReportEmailSet/RemoveItems',
  'Rule/GetRuleMenu',
  'Sensor/GetModel',
  'Statistics/ExportExpireListExcel',
  'Statistics/Get100KiloEventStatistics',
  'Statistics/GetAlarmTypeStatistics',
  'Statistics/GetAlarms',
  'Statistics/GetDayEventStatistics',
  'Statistics/GetEventCompareStatistics',
  'Statistics/GetEventStatistics',
  'Statistics/GetExpireContents',
  'Statistics/GetExpireList',
  'Statistics/GetExpireListInfoNumber',
  'Statistics/GetFuelStatistics',
  'Statistics/GetLicenseSummuyStatistics',
  'Statistics/GetMoveStatistics',
  'Statistics/GetOdoStatistics',
  'Statistics/GetOperateData',
  'Statistics/GetOperationStatistics',
  'Statistics/GetReminderInfo',
  'Statistics/GetSafeStatisticsByCar',
  'Statistics/GetSafeStatisticsByDriver',
  'Statistics/GetStatistics',
  'Statistics/GetSummuyStatistics',
  'Statistics/GetTotalOdoStatistics',
  'Statistics/GetTripSummary',
  'StatisticsV2/GetDashboardAlarmOverview',
  'StatisticsV2/GetDashboardEventCompare',
  'StatisticsV2/GetDashboardTripOverview',
  'SystemRecord/Export',
  'SystemRecord/GetList',
  'Template/GetAuthTemplate',
  'Template/GetList',
  'Template/GetModel',
  'Template/RemoveItems',
  'TrackerDefine/GetModel',
  'Traffics/ExportTransferRecordsToExcel',
  'Traffics/GetTransferRecords2',
  'User/ExportTransferRecordsToExcel',
  'User/ExportTransferRecordsToExcel2',
  'User/GetSubUsersTrackers',
  'User/GetTransferRecords',
  'User/GetTransferRecords2',
  'User/GetUsers',
  'Vertex/GetBindList',
  'Vertex/GetModel',
]

function shouldRemoveSAccountFromQuery(url) {
  const target = url || ''
  return S_ACCOUNT_REMOVE_URLS.some((u) => {
    const i = target.indexOf(u)
    if (i === -1) {
      return false
    }
    const after = target[i + u.length]
    return !after || !/[A-Za-z0-9_]/.test(after)
  })
}

// 后端从请求体 [FromBody] 读取 sssids 的批量下发接口：不从 JSON body 复制 X-Sssids 请求头。
// 设备多时（数百台）拼接出的单条 X-Sssids 头可达几十 KB，超过服务器请求头上限
// （Cloudflare 约 16KB / Kestrel 默认 32KB）会被直接断连，前端表现为 "Network Error"。
const BODY_HEADER_SKIP_URLS = [
  'Command/SendBatchMeitrackCommand',
  'Command/SendBatchMeitrackCommandAsync2',
  'Command/SendBatchMeitrackCommandByHex',
]

function shouldSkipBodyHeaderPromotion(url) {
  const target = url || ''
  return BODY_HEADER_SKIP_URLS.some((u) => {
    const i = target.indexOf(u)
    if (i === -1) {
      return false
    }
    const after = target[i + u.length]
    return !after || !/[A-Za-z0-9_]/.test(after)
  })
}

/**
 * 将请求中敏感参数移动到请求头
 * @param {object} config - axios 请求配置
 */
export function applySecurityHeaders(config) {
  config.headers['language'] = localStorage.getItem('language')

  // 目标账号优先于当前登录账号，避免迁移到请求头时改变接口语义
  const explicitAccount = config.headers['X-Account']
  const requestedAccount = shouldRemoveSAccountFromQuery(config.url)
    ? config.params?.sAccount || config.params?.sAccouont
    : ''
  config.headers['X-Account'] =
    explicitAccount || requestedAccount || localStorage.getItem('sssid') || ''

  if (config.params) {
    // 已加固接口：当前账号参数由 X-Account 头传递，不从查询串取；
    // 未加固接口仍从查询串读取 sAccount，必须保留（目标账号语义）
    if (shouldRemoveSAccountFromQuery(config.url)) {
      delete config.params.sAccount
      delete config.params.sAccouont
    }

    // 敏感参数：从 params 移入对应的请求头
    for (const key in HEADER_MAP) {
      if (Object.prototype.hasOwnProperty.call(HEADER_MAP, key) && config.params[key]) {
        if (shouldKeepParamInQuery(key, config.url)) {
          // 该接口仍从查询串读取该参数 → 保留参数，同时补请求头
          config.headers[HEADER_MAP[key]] = normalizeListValue(String(config.params[key]))
          continue
        }
        config.headers[HEADER_MAP[key]] = normalizeListValue(String(config.params[key]))
        delete config.params[key]
      }
    }
  }

  // 兼容旧调用：将 URL 查询串中内嵌的敏感参数移至请求头（如 dataUrl 拼串场景）
  if (config.url) {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const urlObj = new URL(config.url, baseOrigin)
    const hadProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(config.url)
    const query = urlObj.searchParams
    let urlChanged = false

    for (const key in HEADER_MAP) {
      if (Object.prototype.hasOwnProperty.call(HEADER_MAP, key) && query.get(key)) {
        config.headers[HEADER_MAP[key]] = normalizeListValue(query.get(key))
        if (!shouldKeepParamInQuery(key, config.url)) {
          query.delete(key)
          urlChanged = true
        }
      }
    }

    // sAccount 不再走 URL 查询串（仅限已加固接口；未加固接口仍从查询串读取）
    if (shouldRemoveSAccountFromQuery(config.url)) {
      if (query.get('sAccount')) {
        query.delete('sAccount')
        urlChanged = true
      }
      if (query.get('sAccouont')) {
        query.delete('sAccouont')
        urlChanged = true
      }
    }

    if (urlChanged) {
      const search = urlObj.search
      // 保留原 URL 的绝对/相对形态，避免绝对URL被改写后按网站 origin 解析
      config.url = (hadProtocol ? urlObj.origin : '') + urlObj.pathname + (search ? search : '')
    }
  }

  // 处理 FormData 中的敏感参数（POST + FormData 场景）
  // 注意：只"复制"到请求头，不删除 body 字段——后端加固是分批进行的，
  // 部分接口(如 Location/GetDetailPic)仍从 body 取参数，删除会导致 400。
  if (config.data instanceof FormData) {
    for (const key in HEADER_MAP) {
      if (!Object.prototype.hasOwnProperty.call(HEADER_MAP, key)) {
        continue
      }
      const values = config.data.getAll(key)
      if (values && values.length > 0 && !config.headers[HEADER_MAP[key]]) {
        config.headers[HEADER_MAP[key]] = values.map(normalizeListValue).join(',')
      }
    }
  } else if (
    config.data &&
    typeof config.data === 'object' &&
    !Array.isArray(config.data) &&
    !shouldSkipBodyHeaderPromotion(config.url)
  ) {
    // JSON body：同样只复制到请求头，保留 body
    // （批量下发接口除外，见 BODY_HEADER_SKIP_URLS：body 里本就带 sssids，提升为头会超请求头上限）
    for (const key in HEADER_MAP) {
      if (!Object.prototype.hasOwnProperty.call(HEADER_MAP, key)) {
        continue
      }
      const val = config.data[key]
      if (val !== undefined && val !== null && val !== '' && !config.headers[HEADER_MAP[key]]) {
        config.headers[HEADER_MAP[key]] = normalizeListValue(String(val))
      }
    }
  }
}

export default applySecurityHeaders
